// src/app/pages/cost-metrics/cost-metrics.component.ts
import { Component, OnInit, inject } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';

import { TabsModule } from 'primeng/tabs';
import { SelectModule } from 'primeng/select';
import { CheckboxModule } from 'primeng/checkbox';

import { CostAnalysisComponent } from './cost-analysis/cost-analysis';
import { NetBenefitsComponent } from './net-benefits/net-benefits';
import { CostSavingsComponent } from './cost-saving/cost-savings.component';

import { AnalysisStore } from '../../core/shared/analysis.store';

type RangeKey = 'three' | 'five' | 'ten' | 'fifteen';
type CmShape = Record<string, Record<string, number>>;

// Baseline detector: accepts both old and new names
function isBaselineTitle(title: string | null | undefined): boolean {
    const t = (title || '').trim().toLowerCase();
    return t === 'simulation 1' || t === 'basic simulation';
}

@Component({
    selector: 'app-cost-metric',
    standalone: true,
    imports: [FormsModule, SelectModule, CheckboxModule, TabsModule, NetBenefitsComponent, CostSavingsComponent, CostAnalysisComponent],
    templateUrl: './cost-metrics.component.html'
})
export class costMetrics implements OnInit {
    private http = inject(HttpClient);
    private analysisStore = inject(AnalysisStore);

    yearOptions = [
        { label: 'three', value: 'three' },
        { label: 'five', value: 'five' },
        { label: 'ten', value: 'ten' },
        { label: 'fifteen', value: 'fifteen' }
    ];
    yearSelected: RangeKey = 'three';

    simulationOptions: Array<{ label: string; value: string }> = [];
    currentSimulationId: string | null = null;

    activeTab: string = '1';

    onTabChange(val: string | number | undefined) {
        this.activeTab = String(val ?? '1');
    }

    // Cost metrics for baseline and current
    baseCm: CmShape | null = null;
    public currentCm: CmShape | null = null;

    // KPI fields used by CostSavings template
    public diagnosedCount = 0;
    public diagnosedPercent = 0;

    public treatedCount = 0;
    public treatedPercent = 0;

    public controlledCount = 0;
    public controlledPercent = 0;

    public cvdPatientsCount = 0;
    public cvdPatientsPercent = 0;

    public cvdEventsCount = 0;
    public cvdEventsPercent = 0;

    yearKey: string = '2045';
    hasComparison = false;

    ngOnInit() {
        // Load baseline cost CM from assets (fallback)
        this.http.get<any>('assets/initial-response.json').subscribe({
            next: (resp) => {
                const cm = this.extractCostCmFromResponse(resp);
                this.baseCm = this.clone(cm);
                // console.log('[CostMetrics] Base costCm keys sample', this.baseCm ? Object.keys(this.baseCm).slice(0, 12) : []);
            },
            error: (err) => console.error('[CostMetrics] Failed to load base', err)
        });

        // Build simulations and set current cost CM from latest non-base
        this.analysisStore.runsList$.subscribe((runs) => {
            if (!runs?.length) {
                this.simulationOptions = [];
                this.hasComparison = false;
                this.currentSimulationId = null;
                this.currentCm = null;
                return;
            }

            // Update baseline from store if present
            const baseRun = runs.find((r) => isBaselineTitle(r.title)) ?? runs[0];
            if (baseRun?.costCm) {
                this.baseCm = this.clone(baseRun.costCm);
            }
            const others = runs.filter((r) => !isBaselineTitle(r.title));

            this.simulationOptions = others.map((r) => ({ label: r.title ?? `Simulation ${String(r.id)}`, value: String(r.id) }));
            this.hasComparison = others.length > 0;

            if (others.length > 0) {
                const latest = others[others.length - 1];
                this.currentSimulationId = String(latest.id);

                const run = this.analysisStore.getRunById(this.currentSimulationId!);
                this.currentCm = run?.costCm ?? null;

                // Update KPIs for CostSavings from current run rows
                this.updateKpisFromRun(run);
            } else {
                this.currentSimulationId = null;
                this.currentCm = null;
            }
        });
    }

    onRangeChange(val: RangeKey) {
        this.yearSelected = val;
    }

    onCurrentSimulationChange(runId: string) {
        // console.log('[CostMetrics] onCurrentSimulationChange fired with runId:', runId);
        if (!runId) return;

        this.currentSimulationId = runId;

        const run = this.analysisStore.getRunById(runId);
        this.currentCm = run?.costCm ?? null;

        // Refresh baseline from store if available
        const runs = this.analysisStore.getRunsSnapshot();
        const baseRun = runs.find((r) => (r.title || '').trim().toLowerCase() === 'simulation 1') ?? runs[0];
        if (baseRun?.costCm) {
            this.baseCm = this.clone(baseRun.costCm);
        }

        // Update KPIs
        this.updateKpisFromRun(run);
    }

    // Update KPI counts/percents from selected run rows (favor year 2045)
    private updateKpisFromRun(run: { rows?: any[] } | undefined) {
        const rows = Array.isArray(run?.rows) ? run!.rows : [];
        if (!rows.length) {
            this.diagnosedCount = 0;
            this.diagnosedPercent = 0;
            this.treatedCount = 0;
            this.treatedPercent = 0;
            this.controlledCount = 0;
            this.controlledPercent = 0;
            this.cvdPatientsCount = 0;
            this.cvdPatientsPercent = 0;
            this.cvdEventsCount = 0;
            this.cvdEventsPercent = 0;
            return;
        }

        // pick row for 2045 if present; else the max year
        const byYear = new Map<number, any>();
        rows.forEach((r) => byYear.set(Number(r.year), r));
        const targetYear = byYear.has(2045) ? 2045 : Math.max(...Array.from(byYear.keys()));
        const row = byYear.get(targetYear)!;

        // Counts
        this.diagnosedCount = Number(row.diagnosed_total ?? 0);
        this.treatedCount = Number(row.treated_total ?? 0);
        this.controlledCount = Number(row.controlled_total ?? Number(row.controlled_male ?? 0) + Number(row.controlled_female ?? 0));
        this.cvdPatientsCount = Number(row.cvd_patients_total ?? 0);
        this.cvdEventsCount = Number(row.cvd_events_total ?? 0);

        // Percents (keep 0 if not present)
        this.diagnosedPercent = Number(row.percent_diagnosed ?? 0);
        this.treatedPercent = Number(row.percent_treated ?? 0);
        this.controlledPercent = Number(row.percent_controlled ?? 0);
        this.cvdPatientsPercent = Number(row.percent_cvd_patients ?? 0);
        this.cvdEventsPercent = Number(row.cvd_event_rate ?? 0);
    }

    // Helpers
    private extractCostCmFromResponse(resp: any): CmShape | null {
        // Prefer direct 'cm' when it is clearly cost-related; else deep search for costCm
        const direct = resp?.cm ?? resp?.payload?.[0]?.response?.cm ?? null;
        if (direct && this.containsCostKeys(direct)) return direct;
        const deep = this.findBestCostCmDeep(resp);
        return deep?.cm ?? null;
    }

    private containsCostKeys(cm: any): boolean {
        if (!cm || typeof cm !== 'object') return false;
        const keys = Object.keys(cm).map((k) => k.toLowerCase());
        return keys.some((k) => k.includes('cost') || k.includes('investment') || k.includes('primary') || k.includes('secondary') || k.includes('social'));
    }

    private findBestCostCmDeep(root: any): { cm: CmShape; path: string } | null {
        const tokens = ['cost', 'investment', 'running', 'primary', 'secondary', 'health', 'sector', 'productivity', 'funeral', 'social'];
        const stack: Array<{ node: any; path: string }> = [{ node: root, path: 'root' }];
        const visited = new Set<any>();
        let best: { cm: CmShape; path: string; score: number } | null = null;

        while (stack.length) {
            const { node, path } = stack.pop()!;
            if (!node || typeof node !== 'object') continue;
            if (visited.has(node)) continue;
            visited.add(node);

            const score = this.scoreCostCandidate(node, tokens);
            if (score >= 2) {
                if (!best || score > best.score) best = { cm: node as CmShape, path, score };
            }

            if (Array.isArray(node)) {
                node.forEach((item, i) => stack.push({ node: item, path: `${path}[${i}]` }));
            } else {
                for (const key of Object.keys(node)) {
                    stack.push({ node: (node as any)[key], path: `${path}.${key}` });
                }
            }
        }

        // if (best) console.log('[CostMetrics] Deep costCm found at', best.path);
        return best;
    }

    private scoreCostCandidate(obj: any, tokens: string[]): number {
        if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return 0;
        let score = 0;
        for (const [k, v] of Object.entries(obj)) {
            const key = String(k).toLowerCase();
            if (tokens.some((t) => key.includes(t)) && this.isYearSeries(v)) score++;
        }
        return score;
    }

    private isYearSeries(obj: any): boolean {
        if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return false;
        const keys = Object.keys(obj);
        if (keys.length < 3) return false;
        let count = 0;
        for (const y of keys) if (/^\d{4}$/.test(y) && typeof (obj as any)[y] === 'number') count++;
        return count >= Math.max(3, Math.floor(keys.length * 0.5));
    }

    private clone<T>(obj: T | null): T | null {
        return obj ? JSON.parse(JSON.stringify(obj)) : null;
    }
}
